import { DatePipe, NgClass, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { AdminService } from '../../../admin/admin.service';
import { LoadingComponent } from '../../../loading/loading.component';
import { RippleDirective } from '../../../ripple.directive';
import { DataToolsLiteMessage } from '../../../shared/DataToolsLiteMessage';
import { DatatoolsLiteService } from '../../../shared/services/datatools-lite.service';
import { DuplikitMergeService } from '../../../shared/services/duplikit-merge.service';
import { DuplikitSyncService } from '../../../shared/services/duplikit-sync.service';
import { TimeService } from '../../../shared/services/time.service';
import { UtilsService } from '../../../shared/services/utils.service';
import { DedupeState, SeedState } from '../../dedupe-state/dedupe-state';
import { EventStreamService } from '../../event-stream/event-stream.service';
import { SyncProgressEvent, SyncStage } from '../../sync-progress-event';

@Component({
  selector: 'app-sync',
  templateUrl: './sync.component.html',
  imports: [
    LoadingComponent,
    NgIf,
    NgClass,
    DatePipe,
    MatTooltip,
    RippleDirective
  ],
  styleUrls: ['./sync.component.scss']
})
export class SyncComponent implements OnInit {
  @Input()
  entity: 'Candidate' | 'ClientCorporation' | 'ClientContact';

  dedupeState: DedupeState;
  recentSyncClass = false;
  timeAgo = '';
  showDate = false;

  totalRecords = 0;
  recordsSoFar = 0;
  percentSoFar = 0;
  duplicateCount = 0;

  runningScript = false;
  syncProcessText = '';

  currentSyncStage: SyncStage = 'COMPLETE';
  syncProgress: SyncProgressEvent;

  reportLink: string;

  constructor(
    public router: Router,
    public duplikitSyncService: DuplikitSyncService,
    private eventStreamService: EventStreamService,
    public duplikitMergeService: DuplikitMergeService,
    private timeService: TimeService,
    public utilsService: UtilsService,
    public adminService: AdminService,
    private datatoolsLiteService: DatatoolsLiteService
  ) {
  }

  ngOnInit(): void {
    this.syncProgress = new SyncProgressEvent(0, 0, 0, this.entity, true, 'COMPLETE');
    this.reportLink = 'duplikit/' + this.utilsService.getEntityLower(this.entity) + '-report';
    this.duplikitSyncService.getEntityDedupeState(this.entity).subscribe((dedupeState) => {
      this.dedupeState = dedupeState;

      if (dedupeState.seedState !== SeedState.RUNNING) {
        this.duplikitSyncService.getEntitySyncedRecordCount(this.entity).subscribe((count) => {
          this.recordsSoFar = count;
          this.totalRecords = count;
          this.syncProgress.totalInDB = count;
        });
        this.duplikitSyncService.getEntityDuplicateCount(this.entity).subscribe((count) => this.duplicateCount = count);
      } else {
        const sseStream = this.eventStreamService.getRunningStream(this.getLink('poll'))
          .subscribe((event) => {
            this.syncProcess(event);
          }, (error) => {
            console.error(`Failed to poll ${this.entity} script.`, error);
          }, () => {
            this.syncProcessText = 'Finding duplicates...';
            const scriptStream = this.eventStreamService
              .getScriptStream(this.getLink('scriptDone'))
              .subscribe(() => {
                this.runningScript = true;
                console.log('Script still running.');
              }, (error) => {
                console.error(error);
              }, () => {
                this.runningScript = false;
                console.log('Script complete!');
                this.syncFinal();
                scriptStream.unsubscribe();
                sseStream.unsubscribe();
              });
          });
      }

      this.updateTime();
    });

  }

  updateTime(): void {
    this.timeAgo = this.timeService.getTimeAgo(this.dedupeState.lastRun);
    const conditions = ['days', 'weeks', 'months', 'years'];
    if (conditions.some(el => this.timeAgo.includes(el)))
      this.showDate = true;
    this.recentSyncClass = this.timeService.isRecent(this.dedupeState.lastRun);
  }

  sync(): void {
    if (this.adminService.isInLiteMode()) {
      this.datatoolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DUPLIKIT_SYNC);
      return;
    }

    this.duplikitSyncService.setEntitySyncStatus(this.entity, true);
    this.totalRecords = 0;
    this.dedupeState.seedState = SeedState.RUNNING;
    this.syncProcessText = 'Updating...';

    this.percentSoFar = 0;

    this.duplikitSyncService.getEntityDuplicateCount(this.entity).subscribe((count) => this.duplicateCount = count);

    const sseStream = this.eventStreamService.getSyncStream(this.getLink('sync'),
      this.getLink('poll')).subscribe(event => {
      this.syncProcess(event);
    }, (error) => {
      console.error(error);
    }, () => {
      this.syncProcessText = 'Finding duplicates...';
      const scriptStream = this.eventStreamService
        .getScriptStream(this.getLink('scriptDone'))
        .subscribe(() => {
          this.runningScript = true;
          console.log('Script still running.');
        }, (error) => {
          console.error(error);
        }, () => {
          this.runningScript = false;
          console.log('Script complete!');
          this.syncFinal();
          scriptStream.unsubscribe();
          sseStream.unsubscribe();
        });
    });
  }

  private getLink(l: string) {
    return `${environment.duplikitUrl}/${this.entity}/${l}`;
  }

  private syncProcess(event: SyncProgressEvent): void {
    this.syncProgress = event;
    this.recordsSoFar = event.processed;
    this.totalRecords = event.totalInDB;
    this.currentSyncStage = event.syncStage;

    if (event.totalThisRun !== 0) {
      this.percentSoFar = Math.floor((event.processed / event.totalThisRun) * 100);
    }
  }

  private syncFinal(): void {
    this.duplikitSyncService.getEntityDuplicateCount(this.entity).subscribe((count: number) => {
      this.duplicateCount = count;
      this.dedupeState.lastRun = new Date();
      this.dedupeState.seedState = SeedState.COMPLETE;
      this.updateTime();
      this.duplikitSyncService.setEntitySyncStatus(this.entity, false);
    }, (error) => {
      console.error(`Failed to get duplicate count for ${this.entity}`, error);
    });
  }

  isRunning(state: SeedState): boolean {
    return state === SeedState.RUNNING;
  }

  isComplete(state: SeedState): boolean {
    const isComplete: boolean = state === SeedState.COMPLETE;
    // TODO: Could this go elsewhere?
    if (isComplete) {
      this.percentSoFar = 100;
    }
    return isComplete;
  }

  isNotComplete(state: SeedState): boolean {
    return state !== SeedState.COMPLETE;
  }

  syncLabel(state: SeedState): string {
    if (state === SeedState.RUNNING) {
      return 'Syncing';
    } else if (state === SeedState.COMPLETE) {
      return 'Resync';
    } else {
      return 'Sync';
    }
  }

  pageTitle(state: SeedState): string {
    if (state === SeedState.NOT_RUN) {
      return 'Duplicate Index Empty';
    } else if (state === SeedState.RUNNING) {
      return 'Sync in Progress';
    } else if (state === SeedState.COMPLETE) {
      return 'Database Sync Complete';
    }
  }

  syncText(syncStage: SyncStage): string {
    switch (syncStage) {
      case 'INITIAL_SYNC':
        return 'synchronised';
      case 'INSERTING':
        return 'added';
      case 'UPDATING':
        return 'updated';
      case 'DELETING':
        return 'deleted';
      case 'COMPLETE':
        return 'synchronised';
    }
  }

  get tooltip() {
    return this.adminService.isInLiteMode() === true ? 'This functionality is not available in DataTools Lite' : null;
  }

  canMerge() : boolean {
    return this.duplicateCount > 0
  }
}
